import { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Users, AlertTriangle, RefreshCw, Calendar, Award, Clock, Edit2, Check, X, Search, History } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EmployeeManagement({ user, onLogout }) {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editReason, setEditReason] = useState('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyUser, setHistoryUser] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [stats, setStats] = useState({
    totalEmployees: 0,
    lowBalanceAlerts: 0,
    pendingSync: 0,
    avgPaidLeave: 0,
  });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    applySearch();
  }, [searchTerm, employees]);

  const applySearch = () => {
    if (!searchTerm.trim()) {
      setFilteredEmployees(employees);
      return;
    }
    
    const filtered = employees.filter(e =>
      e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.role.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredEmployees(filtered);
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const employeesData = response.data.filter(u => u.role !== 'Admin');
      setEmployees(employeesData);
      setFilteredEmployees(employeesData);
      
      const total = employeesData.length;
      const lowBalance = employeesData.filter(e => e.paid_leave_balance <= 2).length;
      const avgPaid = total > 0 
        ? (employeesData.reduce((sum, e) => sum + e.paid_leave_balance, 0) / total).toFixed(1)
        : 0;
      
      setStats({
        totalEmployees: total,
        lowBalanceAlerts: lowBalance,
        pendingSync: 0,
        avgPaidLeave: avgPaid,
      });
    } catch (error) {
      toast.error('Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    toast.info('Syncing employee data...');
    
    setTimeout(() => {
      setSyncing(false);
      toast.success('All employee data synced successfully');
      fetchEmployees();
    }, 2000);
  };

  const startEditing = (employee) => {
    setEditingUser(employee.email);
    setEditForm({
      paid_leave_balance: employee.paid_leave_balance,
      unpaid_leaves: employee.unpaid_leaves,
      comp_off_balance: employee.comp_off_balance,
    });
    setEditReason('');
  };

  const cancelEditing = () => {
    setEditingUser(null);
    setEditForm({});
    setEditReason('');
  };

  const saveChanges = async (email) => {
    if (!editReason.trim()) {
      toast.error('Please provide a reason for this change');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/users/${email}/balance`, {
        ...editForm,
        reason: editReason
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Employee balances updated successfully');
      setEditingUser(null);
      setEditReason('');
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update employee');
    }
  };

  const openHistory = async (employee) => {
    setHistoryUser(employee);
    setShowHistoryModal(true);
    setHistoryLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/${employee.email}/balance-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistoryData(response.data);
    } catch (error) {
      toast.error('Failed to fetch history');
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistory = () => {
    setShowHistoryModal(false);
    setHistoryUser(null);
    setHistoryData([]);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="flex-1 md:ml-0">
        {/* Header */}
        <div className="bg-white border-b border-border px-4 md:px-8 py-4 md:py-6 pt-16 md:pt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Employee Balance Management</h1>
              <p className="text-sm md:text-base text-muted-foreground mt-1">Manage leave allocations</p>
            </div>
            <button 
              onClick={handleSyncAll}
              disabled={syncing}
              data-testid="sync-all-button"
              className="flex items-center justify-center gap-2 px-4 py-2 md:py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors shadow-sm disabled:opacity-50 text-sm md:text-base w-full md:w-auto"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              Sync All
            </button>
          </div>
        </div>

        <div className="p-4 md:p-8">
          {/* Stat Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
            <div className="bg-white rounded-xl border border-border p-4 md:p-6 shadow-sm" data-testid="stat-total-employees">
              <div className="flex items-start justify-between mb-2 md:mb-4">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                </div>
              </div>
              <p className="text-xs md:text-sm font-medium text-muted-foreground mb-1">Employees</p>
              <h3 className="text-2xl md:text-4xl font-bold font-heading text-foreground">{stats.totalEmployees}</h3>
            </div>

            <div className="bg-white rounded-xl border border-border p-4 md:p-6 shadow-sm" data-testid="stat-low-balance">
              <div className="flex items-start justify-between mb-2 md:mb-4">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 md:w-5 md:h-5 text-orange-600" />
                </div>
              </div>
              <p className="text-xs md:text-sm font-medium text-muted-foreground mb-1">Low Balance</p>
              <h3 className="text-2xl md:text-4xl font-bold font-heading text-foreground">{stats.lowBalanceAlerts}</h3>
            </div>

            <div className="bg-white rounded-xl border border-border p-4 md:p-6 shadow-sm" data-testid="stat-pending-sync">
              <div className="flex items-start justify-between mb-2 md:mb-4">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <RefreshCw className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
                </div>
              </div>
              <p className="text-xs md:text-sm font-medium text-muted-foreground mb-1">Pending</p>
              <h3 className="text-2xl md:text-4xl font-bold font-heading text-foreground">{stats.pendingSync}</h3>
            </div>

            <div className="bg-white rounded-xl border border-border p-4 md:p-6 shadow-sm" data-testid="stat-avg-paid">
              <div className="flex items-start justify-between mb-2 md:mb-4">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Award className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                </div>
              </div>
              <p className="text-xs md:text-sm font-medium text-muted-foreground mb-1">Avg Paid</p>
              <h3 className="text-2xl md:text-4xl font-bold font-heading text-foreground">{stats.avgPaidLeave}</h3>
            </div>
          </div>

          {/* Search */}
          <div className="bg-white rounded-xl border border-border p-4 md:p-6 mb-6 shadow-sm">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 md:w-5 h-4 md:h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by name, email, or role..."
                data-testid="search-employees"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 md:pl-11 pr-4 py-2 md:py-3 border border-input rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Employee Cards */}
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
              <p className="text-muted-foreground">Loading employees...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {filteredEmployees.map((employee) => (
                <div
                  key={employee.email}
                  className="bg-white rounded-xl border border-border p-4 md:p-6 shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`employee-card-${employee.email}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-full flex items-center justify-center">
                        <span className="text-primary font-semibold text-sm md:text-base">
                          {employee.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-heading font-semibold text-sm md:text-base text-foreground truncate">{employee.name}</h3>
                        <p className="text-xs text-muted-foreground truncate">{employee.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openHistory(employee)}
                        data-testid={`history-${employee.email}`}
                        className="p-2 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                        title="View change history"
                      >
                        <History className="w-4 h-4" />
                      </button>
                      <span className="status-badge border bg-blue-100 text-blue-800 border-blue-200 text-xs">
                        {employee.role}
                      </span>
                    </div>
                  </div>

                  {editingUser === employee.email ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Paid Leave</label>
                          <input
                            type="number"
                            step="0.5"
                            data-testid={`edit-paid-${employee.email}`}
                            value={editForm.paid_leave_balance}
                            onChange={(e) => setEditForm({ ...editForm, paid_leave_balance: parseFloat(e.target.value) })}
                            className="w-full px-2 py-2 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Comp Off</label>
                          <input
                            type="number"
                            step="0.5"
                            data-testid={`edit-compoff-${employee.email}`}
                            value={editForm.comp_off_balance}
                            onChange={(e) => setEditForm({ ...editForm, comp_off_balance: parseFloat(e.target.value) })}
                            className="w-full px-2 py-2 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Unpaid</label>
                          <input
                            type="number"
                            step="0.5"
                            data-testid={`edit-unpaid-${employee.email}`}
                            value={editForm.unpaid_leaves}
                            onChange={(e) => setEditForm({ ...editForm, unpaid_leaves: parseFloat(e.target.value) })}
                            className="w-full px-2 py-2 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-xs text-muted-foreground mb-1">
                          Reason for change <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          data-testid={`edit-reason-${employee.email}`}
                          value={editReason}
                          onChange={(e) => setEditReason(e.target.value)}
                          placeholder="e.g., Annual leave allocation, Correction, etc."
                          className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => saveChanges(employee.email)}
                          data-testid={`save-${employee.email}`}
                          className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                        >
                          <Check className="w-4 h-4" />
                          Save
                        </button>
                        <button
                          onClick={cancelEditing}
                          data-testid={`cancel-${employee.email}`}
                          className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-muted text-foreground rounded-lg text-sm font-medium hover:bg-muted/70 transition-colors"
                        >
                          <X className="w-4 h-4" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-3 gap-2 mb-4">
                        <div className="bg-muted rounded-lg p-3 text-center">
                          <p className="text-xs text-muted-foreground">Paid Leave</p>
                          <p className="text-lg md:text-xl font-bold font-heading text-foreground" data-testid={`paid-${employee.email}`}>
                            {employee.paid_leave_balance}
                          </p>
                        </div>
                        <div className="bg-muted rounded-lg p-3 text-center">
                          <p className="text-xs text-muted-foreground">Comp Off</p>
                          <p className="text-lg md:text-xl font-bold font-heading text-foreground" data-testid={`compoff-${employee.email}`}>
                            {employee.comp_off_balance}
                          </p>
                        </div>
                        <div className="bg-muted rounded-lg p-3 text-center">
                          <p className="text-xs text-muted-foreground">Unpaid</p>
                          <p className="text-lg md:text-xl font-bold font-heading text-foreground" data-testid={`unpaid-${employee.email}`}>
                            {employee.unpaid_leaves}
                          </p>
                        </div>
                      </div>

                      <button
                        onClick={() => startEditing(employee)}
                        data-testid={`edit-${employee.email}`}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-input rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                        Edit Balances
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {filteredEmployees.length === 0 && !loading && (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground">No employees found</p>
            </div>
          )}
        </div>
      </div>

      {/* History Modal */}
      {showHistoryModal && historyUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={closeHistory}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div>
                <h2 className="text-xl font-heading font-bold text-foreground">Balance Change History</h2>
                <p className="text-sm text-muted-foreground">{historyUser.name}</p>
              </div>
              <button
                onClick={closeHistory}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {historyLoading ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-primary mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Loading history...</p>
                </div>
              ) : historyData.length === 0 ? (
                <div className="text-center py-8">
                  <History className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                  <p className="text-muted-foreground">No change history found</p>
                  <p className="text-xs text-muted-foreground mt-1">Changes will appear here when balances are modified</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {historyData.map((item) => (
                    <div
                      key={item.history_id}
                      className="border border-border rounded-lg p-4 bg-muted/30"
                      data-testid={`history-item-${item.history_id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <span className="status-badge border bg-blue-100 text-blue-800 border-blue-200 text-xs">
                          {item.field_changed}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(item.created_at)}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg font-semibold text-red-600">{item.old_value}</span>
                        <span className="text-muted-foreground">→</span>
                        <span className="text-lg font-semibold text-green-600">{item.new_value}</span>
                      </div>
                      
                      <p className="text-sm text-foreground bg-white p-2 rounded border border-border">
                        <span className="text-muted-foreground">Reason:</span> {item.reason}
                      </p>
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        Changed by: {item.changed_by_name}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
